import React, { useState, useEffect, useCallback } from "react";
import { createComment, updateComment } from "./comments.service";
import UserAvatar from "components/user-avatar/user-avatar";
import AutoGrowTextarea from "./auto-grow-textarea";
import { fetchProjectById } from "services/project.service";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Comment } from "types/Comment";
import "./style.scss";
import CommentList from "containers/comments-list/Comments-list";
import { fetchById as fetchFileById } from "services/upload-file.service";
import { fetchById as fetchAssetById } from "services/assets.service";
import { fetchById as fetchByIdOrder } from "services/orders.service";
import { useSelector } from "react-redux";
import { RootState } from "configureStore";

interface CommentProps {
  id: string;
  CommentTypes: string;
}

const Comments: React.FC<CommentProps> = ({ id, CommentTypes = "" }) => {
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();
  const [comment, setComment] = useState<string>("");
  const [newComment, setNewComment] = useState<string>("");
  const [commentList, setCommentList] = useState<Comment[]>([]);
  const [targetComment, setTargetComment] = useState<Comment | null>(null);
  const [isLoading, setIsLoading] = useState<string | undefined>();

  // Watch file entity changes for files to trigger comment refresh
  const { fileEntity } = useSelector(
    (state: RootState) => state.fileContainerReducer
  );
  const { fileEntity: authorizedFileEntity } = useSelector(
    (state: RootState) => state.authorizedFileContainerReducer
  );
  const { session } = useSelector((state: RootState) => state.authReducer);

  const handleFormSubmit = async (commentId?: string) => {
    const formData = {
      comment: commentId ? newComment : comment,
      projectId: CommentTypes === "projects" ? id : null,
      fileId: CommentTypes === "files" ? id : null,
      assetId: CommentTypes === "assets" ? id : null,
      orderId: CommentTypes === "orders" ? id : null,
    };
    const action = commentId ? updateComment : createComment;
    try {
      setIsLoading(commentId ? "update" : "save");
      await action(formData, companyId!, commentId);
      fetchData(id);
      setComment("");
      setTargetComment(null);
      setIsLoading(undefined);
    } catch (e) {
      setIsLoading(undefined);
    }
  };

  const fetchData = useCallback(
    async (id: string) => {
      try {
        setIsLoading("fetch");
        switch (CommentTypes) {
          case "projects": {
            const data = await fetchProjectById(companyId!, id);
            setCommentList(data.comments);
            break;
          }
          case "files": {
            const data = await fetchFileById(id, companyId!);
            setCommentList(data.comments);
            break;
          }
          case "assets": {
            const data = await fetchAssetById(companyId!, id);
            setCommentList(data.comments);
            break;
          }
          case "orders": {
            const { orderEntity } = await fetchByIdOrder(id, companyId!);
            setCommentList(orderEntity.comments);
            break;
          }
          default: {
            break;
          }
        }
        setIsLoading(undefined);
      } catch (err) {
        setIsLoading(undefined);
      }
    },
    [companyId, CommentTypes]
  );

  useEffect(() => {
    if (id) {
      fetchData(id);
    }
  }, [id, fetchData]);

  // Watch file entity changes to trigger comment refresh for files
  useEffect(() => {
    if (CommentTypes === "files" && id) {
      // Check if the file entity matches our current file ID
      const currentFileEntity = fileEntity || authorizedFileEntity;
      if (currentFileEntity && currentFileEntity.id === id) {
        fetchData(id);
      }
    }
  }, [fileEntity, authorizedFileEntity, CommentTypes, id, fetchData]);

  const editHandler = (comment: Comment | null) => {
    setTargetComment(comment);
    setNewComment(comment?.comment || "");
  };

  return (
    <>
      {/* Composer: avatar + textarea + primary action (⌘/Ctrl+Enter to send). */}
      <div className="flex gap-3">
        <div className="shrink-0 pt-0.5">
          <UserAvatar
            companyId={companyId || ""}
            user={{ id: session?.id || "", name: session?.name }}
            size={36}
            rounded="full"
          />
        </div>
        <div className="min-w-0 flex-1">
          <AutoGrowTextarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onKeyDown={(e) => {
              if (
                (e.metaKey || e.ctrlKey) &&
                e.key === "Enter" &&
                comment.trim()
              ) {
                e.preventDefault();
                handleFormSubmit();
              }
            }}
            placeholder={t("write_a_comment", {
              defaultValue: "Write a comment…",
            })}
            className="block w-full resize-none overflow-y-auto rounded-lg border border-[var(--p-color-border)] bg-[var(--p-color-bg-surface)] px-3 py-2 text-base md:text-sm leading-normal text-[var(--p-color-text)] placeholder:text-[var(--p-color-text-secondary)] focus:border-[var(--p-color-border-focus)] focus:outline-none focus:ring-1 focus:ring-[var(--p-color-border-focus)] min-h-[92px] max-h-[180px] [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-black/20"
          />
          <div className="mt-2 flex items-center justify-end gap-3">
            <span className="text-xs text-[var(--p-color-text-secondary)] max-md:hidden">
              {t("cmd_enter_to_send", {
                defaultValue: "⌘/Ctrl + Enter to send",
              })}
            </span>
            <button
              type="button"
              onClick={() => handleFormSubmit()}
              disabled={!comment.trim() || isLoading === "save"}
              className="rounded-lg bg-[var(--p-color-bg-fill-brand)] px-4 py-2 text-sm font-semibold max-md:min-h-11 text-[var(--p-color-text-brand-on-bg-fill)] hover:bg-[var(--p-color-bg-fill-brand-hover)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoading === "save"
                ? t("saving", { defaultValue: "Saving…" })
                : t("make_a_comment")}
            </button>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <CommentList
          comments={commentList}
          targetComment={targetComment}
          newComment={newComment}
          isLoading={isLoading}
          onEdit={editHandler}
          onSave={handleFormSubmit}
          onCancel={() => editHandler(null)}
          onNewCommentChange={setNewComment}
        />
      </div>
    </>
  );
};

export default Comments;
